import { onCLS, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';

interface TelemetryEvent {
  type: 'web-vital' | 'js-error' | 'api-call';
  timestamp: number;
  data: Record<string, unknown>;
}

const buffer: TelemetryEvent[] = [];
const FLUSH_INTERVAL = 30_000; // 30 seconds
const MAX_BUFFER_SIZE = 50;

function getApiBaseUrl(): string {
  // Derive from current page - works in all environments
  const base = document.querySelector('meta[name="api-url"]')?.getAttribute('content');
  if (base) return base;
  // Fallback: same origin with /api prefix
  return `${window.location.origin}/api`;
}

function flush() {
  if (buffer.length === 0) return;
  const events = buffer.splice(0, buffer.length);
  const url = `${getApiBaseUrl()}/telemetry/frontend`;
  const payload = JSON.stringify({ events });

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', body: payload, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => {});
    }
  } catch {
    // Silently fail - telemetry should never break the app
  }
}

function record(event: TelemetryEvent) {
  buffer.push(event);
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Observability]', event.type, event.data);
  }
  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
  }
}

function reportWebVital(metric: Metric) {
  record({
    type: 'web-vital',
    timestamp: Date.now(),
    data: {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
    },
  });
}

/** Track an API call's performance and errors */
export function trackApiCall(method: string, endpoint: string, durationMs: number, status: number | null, error?: string) {
  record({
    type: 'api-call',
    timestamp: Date.now(),
    data: {
      method,
      endpoint: endpoint.replace(/\/[0-9a-f-]{36}/g, '/:id'), // Normalize UUIDs
      duration_ms: durationMs,
      status,
      error: error || undefined,
    },
  });
}

/** Initialize all observability collection */
export function initObservability() {
  // Web Vitals
  try {
    onCLS(reportWebVital);
    onLCP(reportWebVital);
    onTTFB(reportWebVital);
    onINP(reportWebVital);
  } catch {
    // web-vitals may not be available in all environments
  }

  // Global error handler
  window.addEventListener('error', (event) => {
    record({
      type: 'js-error',
      timestamp: Date.now(),
      data: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack?.slice(0, 1000),
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    record({
      type: 'js-error',
      timestamp: Date.now(),
      data: {
        message: reason?.message || String(reason),
        stack: reason?.stack?.slice(0, 1000),
        unhandled_rejection: true,
      },
    });
  });

  // API call tracking via events from shared API client
  window.addEventListener('api:request-complete', ((event: CustomEvent) => {
    const { method, endpoint, durationMs, status, error } = event.detail;
    trackApiCall(method, endpoint, durationMs, status, error);
  }) as EventListener);

  // Periodic flush
  setInterval(flush, FLUSH_INTERVAL);

  // Flush on page unload
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}
